import React, { useEffect, useId, useRef, useState } from "react";
import Countdown from "./CountDown";

const FeeWarning = ({
  time,
  closeFeeWarning,
  onDismiss,
  feeMissingCourses,
  feeMissingAmount,
  feewarningmsg,
  continueLabel = "Continue",
  title = "Fee pending",
  intro = "Your fees for the following courses are missing:",
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const continueRef = useRef(null);
  const canDismissRef = useRef(false);
  const [canDismiss, setCanDismiss] = useState(false);

  const visibleCourses = (feeMissingCourses || [])
    .flat()
    .filter(
      (course) =>
        course &&
        (course.status === "enrolled" || course.status === "pending"),
    );

  useEffect(() => {
    canDismissRef.current = canDismiss;
    if (canDismiss) {
      continueRef.current?.focus();
    }
  }, [canDismiss]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (!canDismissRef.current) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        (onDismiss || closeFeeWarning)?.();
        return;
      }

      if (e.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) {
        e.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeFeeWarning, onDismiss]);

  const handleDismiss = () => {
    (onDismiss || closeFeeWarning)?.();
  };

  return (
    <div className="mycourses-fee-overlay" role="presentation">
      <div
        ref={dialogRef}
        className="mycourses-fee-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="mycourses-fee-dialog__header">
          <div className="mycourses-fee-dialog__header-text">
            <p className="mycourses-fee-dialog__eyebrow">Payment required</p>
            <h2 id={titleId} className="mycourses-fee-dialog__title">
              {title}
            </h2>
          </div>
          {canDismiss ? (
            <button
              type="button"
              className="mycourses-fee-dialog__close"
              onClick={handleDismiss}
              aria-label="Close without opening LMS"
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="mycourses-fee-dialog__body">
          <p className="mycourses-fee-dialog__intro">{intro}</p>

          {visibleCourses.length ? (
            <ul className="mycourses-fee-dialog__list">
              {visibleCourses.map((course) => (
                <li key={course.id || course.title}>
                  {course.title || "Unknown course"}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mycourses-fee-dialog__amount-card">
            <span className="mycourses-fee-dialog__amount-label">
              Amount due
            </span>
            <strong className="mycourses-fee-dialog__amount-value">
              PKR {Number(feeMissingAmount || 0).toLocaleString()}
            </strong>
          </div>

          <p className="mycourses-fee-dialog__disclaimer">
            {feewarningmsg ||
              "Please pay your fees before the due date to avoid any inconvenience."}
          </p>

          <div className="mycourses-fee-dialog__wait">
            <p className="mycourses-fee-dialog__wait-label">
              {canDismiss ? "Ready to continue" : "Please wait before continuing"}
            </p>
            <Countdown
              duration={time}
              onUnlock={() => setCanDismiss(true)}
            />
          </div>

          <button
            ref={continueRef}
            type="button"
            className={`mycourses-fee-dialog__continue${
              canDismiss ? " mycourses-fee-dialog__continue--ready" : ""
            }`}
            onClick={closeFeeWarning}
            disabled={!canDismiss}
            aria-disabled={!canDismiss}
          >
            {canDismiss ? continueLabel : "Please wait…"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeWarning;
